import QRCode from "qrcode";
import JSZip from "jszip";
import { IAsset } from "./models";

// ─── Payload ─────────────────────────────────────────────────────────────────
// Human-readable text record encoded into each QR code. Mirrors the standalone
// "qrcode generator" reference tool so scanned labels look identical.
export type TAssetQrField =
  | "assetNo"
  | "category"
  | "description"
  | "serialNumber"
  | "status"
  | "supplier"
  | "purchaseDate"
  | "totalCost"
  | "assignedTo"
  | "notes";

interface IAssetQrFieldDef {
  key: TAssetQrField;
  label: string;
  // Returns the printable value, or undefined when the asset has nothing to show.
  value: (asset: Partial<IAsset>) => string | undefined;
}

function formatPurchaseDate(raw: string): string {
  const d = new Date(raw);
  return isNaN(d.getTime()) ? String(raw) : d.toISOString().slice(0, 10);
}

// Declaration order here is the order lines appear inside the QR payload,
// regardless of the order the user ticked the boxes.
export const ASSET_QR_FIELDS: IAssetQrFieldDef[] = [
  { key: "assetNo", label: "Asset No", value: (a) => a.assetNo || undefined },
  { key: "category", label: "Category", value: (a) => a.category || undefined },
  {
    key: "description",
    label: "Description",
    value: (a) => a.description || undefined,
  },
  {
    key: "serialNumber",
    label: "Serial Number",
    value: (a) => a.serialNumber || undefined,
  },
  { key: "status", label: "Status", value: (a) => a.status || undefined },
  { key: "supplier", label: "Supplier", value: (a) => a.supplier || undefined },
  {
    key: "purchaseDate",
    label: "Date of Purchase",
    value: (a) => (a.purchaseDate ? formatPurchaseDate(a.purchaseDate) : undefined),
  },
  {
    key: "totalCost",
    label: "Total Cost of Asset (Gross)",
    value: (a) =>
      a.totalCost === undefined || a.totalCost === null
        ? undefined
        : `${a.totalCost} ${a.currency || ""}`.trim(),
  },
  {
    key: "assignedTo",
    label: "Assigned To",
    value: (a) =>
      a.assignedTo?.name
        ? `${a.assignedTo.name}${
            a.assignedTo.department ? ` (${a.assignedTo.department})` : ""
          }`
        : undefined,
  },
  { key: "notes", label: "Comments", value: (a) => a.notes || undefined },
];

export const ASSET_QR_FIELD_KEYS: TAssetQrField[] = ASSET_QR_FIELDS.map(
  (f) => f.key
);

export const DEFAULT_ASSET_QR_FIELDS: TAssetQrField[] = [
  "assetNo",
  "category",
  "description",
  "serialNumber",
  "status",
  "purchaseDate",
  "totalCost",
];

export function buildAssetPayload(
  asset: Partial<IAsset>,
  fields: TAssetQrField[] = DEFAULT_ASSET_QR_FIELDS
): string {
  // An empty selection would encode a header and nothing else, so fall back.
  const selected = new Set(
    fields.length > 0 ? fields : DEFAULT_ASSET_QR_FIELDS
  );
  const lines = ["Asset Record"];

  ASSET_QR_FIELDS.forEach((field) => {
    if (!selected.has(field.key)) return;
    const value = field.value(asset);
    if (value) lines.push(`${field.label}: ${value}`);
  });

  return lines.join("\n");
}

function sanitizeFilename(name: string): string {
  return name.replace(/[/\\?%*:|"<>]/g, "-");
}

// ─── Rendering ───────────────────────────────────────────────────────────────
// Draw a QR (from the `qrcode` package) onto a larger canvas with the asset
// number printed beneath it, returning a PNG data URL.
async function buildLabelledDataUrl(
  text: string,
  label: string,
  size = 260
): Promise<string> {
  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, text, {
    width: size,
    margin: 1,
    errorCorrectionLevel: "M",
    color: { dark: "#0f172a", light: "#ffffff" },
  });

  const padding = 14;
  const fontSize = 16;
  const lineHeight = fontSize + 8;

  const out = document.createElement("canvas");
  out.width = qrCanvas.width + padding * 2;
  out.height = qrCanvas.height + lineHeight + padding * 2;

  const ctx = out.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(qrCanvas, padding, padding);

  ctx.fillStyle = "#0f172a";
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText(label, out.width / 2, qrCanvas.height + padding + lineHeight - 2);

  return out.toDataURL("image/png");
}

// Preview data URL (no label) for on-screen display.
export async function buildQrDataUrl(text: string, size = 220): Promise<string> {
  return QRCode.toDataURL(text, {
    width: size,
    margin: 1,
    errorCorrectionLevel: "M",
    color: { dark: "#0f172a", light: "#ffffff" },
  });
}

function triggerDownload(dataUrl: string, filename: string) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ─── Downloads ───────────────────────────────────────────────────────────────
export async function downloadAssetQr(
  asset: Partial<IAsset>,
  fields?: TAssetQrField[]
): Promise<void> {
  const assetNo = asset.assetNo || "asset";
  const dataUrl = await buildLabelledDataUrl(
    buildAssetPayload(asset, fields),
    assetNo,
    260
  );
  triggerDownload(dataUrl, `${sanitizeFilename(assetNo)}-qr.png`);
}

// Bundle many QR codes into a single ZIP for bulk download.
export async function downloadAssetsQrZip(
  assets: Partial<IAsset>[],
  fields?: TAssetQrField[]
): Promise<number> {
  const zip = new JSZip();
  let count = 0;

  for (let i = 0; i < assets.length; i += 1) {
    const asset = assets[i];
    const assetNo = asset.assetNo || `asset-${i + 1}`;
    // eslint-disable-next-line no-await-in-loop
    const dataUrl = await buildLabelledDataUrl(
      buildAssetPayload(asset, fields),
      assetNo,
      260
    );
    const base64 = dataUrl.split(",")[1];
    if (base64) {
      zip.file(`${sanitizeFilename(assetNo)}-qr.png`, base64, { base64: true });
      count += 1;
    }
  }

  if (count === 0) return 0;

  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `sema-asset-qr-codes-${Date.now()}.zip`);
  setTimeout(() => URL.revokeObjectURL(url), 5000);

  return count;
}
